import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode } from "jwt-decode";

type AuthUser = {
  id: string;
  name: string;
  email?: string;
  roles?: string[];
};

type AuthState = {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
};

type StateWithAuth = { auth: AuthState };

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login(
      state,
      action: PayloadAction<{ user: AuthUser; token: string }>
    ) {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
    },
    logout(state) {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
    },
    clear(state) {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
    },
    setToken(state, action: PayloadAction<string>) {
      state.token = action.payload;
    },
  },
});

export const { login, logout, clear, setToken } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectToken = (state: StateWithAuth) => state.auth.token;
export const selectIsAuthenticated = (state: StateWithAuth) =>
  state.auth.isAuthenticated;
export const selectUser = (state: StateWithAuth) => state.auth.user;

export const selectIsTokenExpired = (state: StateWithAuth): boolean => {
  const token = state.auth.token;
  if (!token) return true;
  try {
    const { exp } = jwtDecode<{ exp?: number }>(token);
    if (!exp) return false;
    return Date.now() / 1000 > exp;
  } catch {
    return true;
  }
};

export const selectIsValidSession = createSelector(
  [selectIsAuthenticated, selectIsTokenExpired],
  (isAuthenticated, isExpired) => isAuthenticated && !isExpired
);
